'use client';
import { useResource } from '@/lib/use-resource';
import type { ProjectionHorizon } from '@/lib/projectScore';

interface MonthsResp { months: string[]; }

interface Props {
  /** Selected past month (YYYY-MM) or null for live/projection. */
  historicalMonth: string | null;
  /** Forward projection horizon (only meaningful when historicalMonth is null). */
  projectionHorizon: ProjectionHorizon;
  /** When user selects a past month. */
  onHistoricalMonthChange: (month: string | null) => void;
  /** When user selects a forward projection. */
  onProjectionChange: (h: ProjectionHorizon) => void;
  /** Disabled state while a historical fetch is in flight. */
  loading?: boolean;
}

/** Format YYYY-MM as e.g. "Apr 2024" or "Now" / "+1Y" / "+2Y". */
function fmtStop(s: string): string {
  if (s === 'now') return 'NOW';
  if (s === '+1Y') return '+1Y';
  if (s === '+2Y') return '+2Y';
  const [y, mm] = s.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(mm,10)-1]} ${y}`;
}

/**
 * Top-right time slider for the home-page 3D scatter. Combines:
 *   - Monthly historical snapshots from /api/full-monthly (back to 2015-01)
 *   - "NOW" (live data)
 *   - "+1Y" / "+2Y" forward projection horizons
 * Replaces the prior 1D/1W/.../2Y button row and Now/+1Y/+2Y projection row.
 */
export function HomeTimeSlider({
  historicalMonth,
  projectionHorizon,
  onHistoricalMonthChange,
  onProjectionChange,
  loading,
}: Props) {
  const monthsResp = useResource<MonthsResp>('/api/full-monthly');
  const months = monthsResp.data?.months ?? [];
  const stops: string[] = [...months, 'now', '+1Y', '+2Y'];

  // Map current state → slider index
  let stopIdx = stops.length - 3; // default to "now"
  if (historicalMonth) {
    const i = stops.indexOf(historicalMonth);
    if (i >= 0) stopIdx = i;
  } else if (projectionHorizon === '+1Y') {
    stopIdx = stops.length - 2;
  } else if (projectionHorizon === '+2Y') {
    stopIdx = stops.length - 1;
  }
  const stop = stops[stopIdx] ?? 'now';

  const dispatch = (i: number) => {
    const s = stops[i];
    if (s === 'now') { onHistoricalMonthChange(null); onProjectionChange('now'); }
    else if (s === '+1Y') { onHistoricalMonthChange(null); onProjectionChange('+1Y'); }
    else if (s === '+2Y') { onHistoricalMonthChange(null); onProjectionChange('+2Y'); }
    else { onProjectionChange('now'); onHistoricalMonthChange(s); }
  };

  if (months.length === 0) return null;

  return (
    <div
      className="flex flex-col items-end gap-1 px-2.5 py-2 rounded-lg"
      style={{ background: 'rgba(15, 17, 23, 0.85)', border: '1px solid #1e293b' }}
    >
      <div className="flex items-center gap-2 text-[10px] font-mono">
        <span className="uppercase tracking-wider text-[#64748b]">View</span>
        <button
          onClick={() => dispatch(Math.max(0, stopIdx - 12))}
          disabled={loading}
          className="text-[#64748b] hover:text-amber-300 px-1 disabled:cursor-wait"
          title="Back 1 year"
        >‹‹</button>
        <button
          onClick={() => dispatch(Math.max(0, stopIdx - 1))}
          disabled={loading}
          className="text-[#64748b] hover:text-amber-300 px-1 disabled:cursor-wait"
        >‹</button>
        <input
          type="range"
          min={0}
          max={stops.length - 1}
          value={stopIdx}
          onChange={(e) => dispatch(parseInt(e.target.value, 10))}
          disabled={loading}
          className="w-[260px] accent-amber-400 disabled:cursor-wait"
        />
        <button
          onClick={() => dispatch(Math.min(stops.length - 1, stopIdx + 1))}
          disabled={loading}
          className="text-[#64748b] hover:text-amber-300 px-1 disabled:cursor-wait"
        >›</button>
        <button
          onClick={() => dispatch(Math.min(stops.length - 1, stopIdx + 12))}
          disabled={loading}
          className="text-[#64748b] hover:text-amber-300 px-1 disabled:cursor-wait"
          title="Forward 1 year"
        >››</button>
        <button
          onClick={() => dispatch(stops.indexOf('now'))}
          disabled={loading}
          className="text-amber-300/70 hover:text-amber-300 uppercase tracking-wide font-mono"
        >Now</button>
        <span
          className="font-mono font-semibold min-w-[5rem] text-right"
          style={{
            color:
              stop === '+1Y' || stop === '+2Y' ? '#a855f7' :
              stop === 'now' ? '#fbbf24' :
              '#94a3b8',
          }}
        >
          {fmtStop(stop)}
        </span>
      </div>
      {/* Year ticks */}
      <div className="flex items-center justify-end gap-0.5 text-[8px] text-[#475569] font-mono">
        {stops.map((s, i) => {
          const isJan = s.endsWith('-01');
          const isCurrent = i === stopIdx;
          if (!isJan && !isCurrent && s !== 'now' && s !== '+1Y' && s !== '+2Y') return null;
          return (
            <button
              key={s + '-' + i}
              onClick={() => dispatch(i)}
              className={`px-1 cursor-pointer transition-colors hover:text-[#94a3b8] ${isCurrent ? 'text-amber-300' : ''}`}
            >
              {s === 'now' ? 'NOW' : s === '+1Y' ? '+1Y' : s === '+2Y' ? '+2Y' : s.slice(0, 4)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
